import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  PackageCheck,
  IndianRupee,
  Scale,
  TrendingDown,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_grn_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

// Backend ab variance khud bhejta hai, qty ingredient ke apne unit me.
// GRN level pe alag unit ki qty (KG + ML) jodna galat tha — ab "kitne item
// kam aaye" (negative = short) dikhate hain.
function withVariance(grn) {
  const items = (grn.items || []).map((item) => {
    const ordered = parseFloat(item.ordered_qty) || 0;
    const received = parseFloat(item.received_qty) || 0;
    const variance_qty =
      item.variance_qty !== undefined
        ? parseFloat(item.variance_qty) || 0
        : received - ordered;
    const variance_percentage =
      item.variance_pct ??
      item.variance_percentage ??
      (ordered > 0 ? (variance_qty / ordered) * 100 : 0);

    return {
      ...item,
      variance_qty,
      variance_percentage,
      base_price: item.rate ?? item.base_price,
      line_amount: item.amount ?? item.line_amount,
    };
  });

  const itemsShort =
    grn.items_short ?? items.filter((i) => i.variance_qty < 0).length;

  return {
    ...grn,
    items,
    total_variance: -itemsShort,
    grn_amount: grn.item_amount ?? grn.grn_amount,
    department_name: grn.received_at || grn.department_name,
  };
}

export default function GRNReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [vendorId, setVendorId] = useState("all");
  const [poId, setPoId] = useState("");

  // Filter dropdown data
  const [vendors, setVendors] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [grnData, setGrnData] = useState([]);
  const [expandedGrnId, setExpandedGrnId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary; variance is derived client-side, see below)
  const [stats, setStats] = useState({
    totalGrns: 0,
    totalGrnAmount: 0,
    totalOrderedQty: 0,
    totalReceivedQty: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (vendors)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        // vendors key get_report_filter_data mein add karni padegi backend pe
        setVendors(response.data.message.vendors || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch GRN data
  const fetchGrnData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (vendorId !== "all") fd.append("vendor_id", vendorId);
      if (poId.trim() !== "") fd.append("po_id", poId.trim());

      try {
        const response = await server_post_data(get_grn_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const rawData = response.data.data || [];
          const data = rawData.map(withVariance);
          const summary = response.data.summary || {};
          setGrnData(data);
          setExpandedGrnId(null);
          setCurrentPage(1);
          // Qty jodna band — items count
          const totalItems =
            summary.total_items ??
            data.reduce((s, g) => s + (g.items || []).length, 0);
          const shortItems =
            summary.items_received_short ??
            data.reduce((s, g) => s + Math.abs(g.total_variance || 0), 0);

          setStats({
            totalGrns: summary.total_grns || 0,
            totalGrnAmount:
              summary.total_amount ?? summary.total_grn_amount ?? 0,
            totalOrderedQty: totalItems,
            totalReceivedQty: totalItems - shortItems,
          });
        }
      } catch (error) {
        console.error("Error fetching GRN data:", error);
        toast({
          title: "Error",
          description: "Failed to load GRN data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, vendorId, poId, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchGrnData(startCal, endCal, "2");
  }, [startCal, endCal, fetchGrnData]);

  // Overall variance, derived client-side from the per-GRN totals
  const totalVariance = useMemo(
    () => grnData.reduce((sum, g) => sum + (g.total_variance || 0), 0),
    [grnData],
  );

  // Excel export - 2 sheets (GRN summary + item level detail)
  function downloadExcel(orderData, filename = "GRN_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    // Sheet 1: GRN level
    const grnSheetData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      "PO / GRN Number": item.po_number || "-",
      "GRN Date": item.grn_date || "-",
      Vendor: item.vendor_name || "-",
      Department: item.department_name || "-",
      "Challan No": item.challan_no || "-",
      "Total Items": item.total_items,
      "GRN Amount (₹)": formatNumber(item.grn_amount),
      "Grand Amount (₹)": formatNumber(item.grand_amount),
      "Items Short": Math.abs(item.total_variance || 0),
      "Created By": item.created_by || "-",
      Remarks: item.remarks || "-",
    }));

    grnSheetData.push({
      "Sr. No.": "",
      "PO / GRN Number": "",
      "GRN Date": "",
      Vendor: "",
      Department: "",
      "Challan No": "",
      "Total Items": "TOTAL",
      "GRN Amount (₹)": formatNumber(stats.totalGrnAmount),
      "Grand Amount (₹)": "",
      "Items Short": Math.abs(totalVariance),
      "Created By": `Total GRNs: ${stats.totalGrns}`,
      Remarks: "",
    });

    // Sheet 2: Item level with variance
    const itemSheetData = [];
    orderData.forEach((grn) => {
      (grn.items || []).forEach((item) => {
        itemSheetData.push({
          "PO / GRN Number": grn.po_number || "-",
          Vendor: grn.vendor_name || "-",
          Ingredient: item.ingredient_name || "-",
          "Ordered Qty": formatNumber(item.ordered_qty),
          "Received Qty": formatNumber(item.received_qty),
          "Variance Qty": formatNumber(item.variance_qty),
          "Variance %": formatNumber(item.variance_percentage),
          Unit: item.unit || "-",
          "Rate (₹)": formatNumber(item.base_price),
          "Amount (₹)": formatNumber(item.line_amount),
        });
      });
    });

    const grnSheet = XLSX.utils.json_to_sheet(grnSheetData);
    grnSheet["!cols"] = [
      { wch: 8 },
      { wch: 16 },
      { wch: 12 },
      { wch: 20 },
      { wch: 18 },
      { wch: 14 },
      { wch: 11 },
      { wch: 15 },
      { wch: 15 },
      { wch: 13 },
      { wch: 18 },
      { wch: 25 },
    ];

    const itemSheet = XLSX.utils.json_to_sheet(itemSheetData);
    itemSheet["!cols"] = [
      { wch: 16 },
      { wch: 20 },
      { wch: 22 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 11 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, grnSheet, "GRN Summary");
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item Variance");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(grnData.length / entriesPerPage);
  const currentData = grnData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setExpandedGrnId(null);
  };

  const toggleExpand = (grnId) => {
    setExpandedGrnId(expandedGrnId === grnId ? null : grnId);
  };

  // Format date for table display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const varianceClass = (variance) => {
    if (variance > 0) return "text-warning";
    if (variance < 0) return "text-destructive";
    return "text-success";
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchGrnData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="GRN Report"
        subtitle="Goods Received Notes with ordered vs received variance analysis"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <PackageCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalGrns}
                </p>
                <p className="text-sm text-muted-foreground">Total GRNs</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalGrnAmount)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total GRN Amount
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatNumber(stats.totalReceivedQty)} /{" "}
                  {formatNumber(stats.totalOrderedQty)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Items Fully Received
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p
                  className={cn(
                    "text-2xl font-bold",
                    varianceClass(totalVariance),
                  )}
                >
                  {totalVariance > 0 ? "+" : ""}
                  {formatNumber(totalVariance)}
                </p>
                <p className="text-sm text-muted-foreground">Items Short</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Vendor */}
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Select value={vendorId} onValueChange={setVendorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem
                        key={vendor.primary_id}
                        value={String(vendor.primary_id)}
                      >
                        {vendor.vendor_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* PO ID */}
              <div className="space-y-2">
                <Label>PO ID</Label>
                <Input
                  type="text"
                  placeholder="Enter PO ID"
                  value={poId}
                  onChange={(e) => setPoId(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(grnData)}
                  className="gap-2"
                  disabled={grnData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-10"></TableHead>
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    PO / GRN Number
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    GRN Date
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Vendor
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Challan No
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Items
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    GRN Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Grand Amount (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Items Short
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Created By
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <Fragment key={row.grn_id || index}>
                      <TableRow
                        className="border-border cursor-pointer"
                        onClick={() => toggleExpand(row.grn_id)}
                      >
                        <TableCell>
                          {expandedGrnId === row.grn_id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.po_number || "-"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(row.grn_date)}
                        </TableCell>
                        <TableCell>{row.vendor_name || "-"}</TableCell>
                        <TableCell>{row.department_name || "-"}</TableCell>
                        <TableCell>{row.challan_no || "-"}</TableCell>
                        <TableCell className="text-right">
                          {row.total_items}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatNumber(row.grn_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.grand_amount)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            varianceClass(row.total_variance),
                          )}
                        >
                          {row.total_variance > 0 ? "+" : ""}
                          {formatNumber(row.total_variance)}
                        </TableCell>
                        <TableCell>{row.created_by || "-"}</TableCell>
                      </TableRow>

                      {/* Expanded item detail */}
                      {expandedGrnId === row.grn_id && (
                        <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={12} className="p-4">
                            {row.remarks && (
                              <div className="mb-3 text-xs text-muted-foreground">
                                Remarks: {row.remarks}
                              </div>
                            )}
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-border hover:bg-transparent">
                                    <TableHead className="text-muted-foreground text-xs">
                                      Ingredient
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Ordered
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Received
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Variance
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Variance %
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Unit
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Rate (₹)
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Amount (₹)
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(row.items || []).map((item, i) => (
                                    <TableRow
                                      key={`${row.grn_id}-${item.ingredient_id}-${i}`}
                                      className="border-border"
                                    >
                                      <TableCell className="font-medium text-sm">
                                        {item.ingredient_name || "-"}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(item.ordered_qty)}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(item.received_qty)}
                                      </TableCell>
                                      <TableCell
                                        className={cn(
                                          "text-right text-sm font-medium",
                                          varianceClass(item.variance_qty),
                                        )}
                                      >
                                        {item.variance_qty > 0 ? "+" : ""}
                                        {formatNumber(item.variance_qty)}
                                      </TableCell>
                                      <TableCell
                                        className={cn(
                                          "text-right text-sm font-medium",
                                          varianceClass(item.variance_qty),
                                        )}
                                      >
                                        {item.variance_qty > 0 ? "+" : ""}
                                        {formatNumber(item.variance_percentage)}
                                        %
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {item.unit || "-"}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(item.base_price)}
                                      </TableCell>
                                      <TableCell className="text-right text-sm font-semibold">
                                        {formatNumber(item.line_amount)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, grnData.length)} of{" "}
                {grnData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
